import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IIdValuePair } from 'src/app/core/interfaces/common/common/id-value-pair.interface';
import { IOptions } from 'src/app/core/interfaces/common/common/options.interface';
import { IStateProvince } from 'src/app/core/interfaces/common/common/stateProvince.interface';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IDropdownOption } from 'src/app/core/interfaces/common/ui/dropdown-option.interface';
import { IMultiOptions } from 'src/app/core/interfaces/onboarding/business-profile/business-multi-options.interface';
import { ILegacyUpgrade } from 'src/app/core/interfaces/onboarding/legacy-upgrade/legacy-upgrade.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { Address } from 'src/app/core/models/common/address.model';
import { PageContent } from 'src/app/core/models/common/page-content.model';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { CommonService } from 'src/app/core/services/common/common.service';
import { HydrationService } from 'src/app/core/services/login/hydration.service';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { LegacyUpgradeService } from 'src/app/core/services/onboarding/legacy-upgrade.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import {
  HydrationRoles,
  HydrationSources,
} from 'src/app/core/store/common/interfaces/hydration-status-store.interface';
import * as BusinessProfileActions from 'src/app/core/store/onboarding/actions/business-profile.actions';
import * as CreateAccountActions from 'src/app/core/store/onboarding/actions/create-account.actions';
import { selectAllBusinessProfile } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { selectAllCreateAccount } from 'src/app/core/store/onboarding/reducers+selectors/create-account.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { ToolTipComponent } from 'src/app/shared/common/tool-tip/tool-tip.component';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-legacy-upgrade',
  templateUrl: './legacy-upgrade.component.html',
  styleUrls: ['./legacy-upgrade.component.scss'],
})
export class LegacyUpgradeComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  @ViewChild(ToolTipComponent) headerTooltipComponent: ToolTipComponent;
  @ViewChild('content', { static: true }) content: ElementRef<HTMLDivElement>;
  @ViewChild('container', { static: true })
  container: ElementRef<HTMLDivElement>;

  form1: FormGroup;
  isForm1: boolean;
  form2: FormGroup;
  isForm2: boolean;
  lendingFormGroup: FormGroup;
  backState: PageState;
  nextState: PageState;
  backText: string;
  headerText: string;
  tooltip1Content: IPageContent;

  // if text is not empty, display its field
  resolveRegisteredCompanyNameText: string;
  resolveHowDidYouHearAboutUsText: string;
  resolveSetPassword1Text: string;
  resolveSetPassword2Text: string;
  resolveBusinessIndustryText: string;
  resolveCustomerTypeText: string;
  resolveStateOfOrgText: string;
  resolveAddressText: string;
  resolveCompanyURLText: string;
  resolveNumberOfEmployeesText: string;
  resolveLendingMethodText: string;
  resolveInvoicingMethodText: string;

  defaultRegisteredCompanyNameText: string;
  defaultBusinessIndustryText: string;
  defaultCustomerTypeText: string;
  defaultStateOfOrg: IDropdownOption;
  defaultAddressState: IDropdownOption;
  defaultAddressText: string;
  defaultCompanyURLText: string;
  defaultNumberOfEmployeesText: string;
  defaultLendingMethodText: string;
  defaultInvoicingMethodText: string;

  heardAboutUsOptions: IDropdownOption[] = [];
  businessIndustryOptions: IDropdownOption[] = [];
  customerTypeOptions: IMultiOptions[] = [];
  stateOptions: IDropdownOption[] = [];
  numberOfEmployeesOptions: IDropdownOption[] = [];
  lendingMethodOptions: IMultiOptions[] = [];
  invoicingMethodOptions: IDropdownOption[] = [];

  heardAboutUsReferralBit = false;
  heardAboutUsOtherBit = false;
  businessIndustryOthersBit = false;
  customerTypeOthersBit = false;
  invoicingMethodOthersBit = false;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private lus: LegacyUpgradeService,
    private bps: BusinessProfileService,
    private ees: EnrollmentExtendedService,
    private commonService: CommonService,
    private hs: HydrationService,
    private fb: FormBuilder,
    private store: Store<IAppState>,
  ) {}

  // wrapping all the option loading in Promise.all
  // OR using a resolver will help
  // alleviate the initial latency
  ngOnInit(): void {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);

        this.heardAboutUsOptions = this.mapOptionsToDropdownOptions(
          data['heardAboutUsOptions'],
        );
        this.businessIndustryOptions = this.mapOptionsToDropdownOptions(
          data['businessIndustryOptions'],
        );
        this.customerTypeOptions = await this.bps.loadCustomerType();
        this.stateOptions = this.mapStatesToDropdownOptions(
          data['stateProvinceOptions'],
        );
        this.numberOfEmployeesOptions = this.mapOptionsToDropdownOptions(
          data['numberOfEmployeesOptions'],
        );
        this.lendingMethodOptions = data['supportMethodOptions'];
        this.invoicingMethodOptions = this.mapOptionsToDropdownOptions(
          data['businessInvoiceOptions'],
        );
      }),
    );

    this.form1 = this.fb.group({
      registeredCompanyName: ['', [Validators.required]],
      howDidYouHearAboutUsId: [null, [Validators.required]],
      howDidYouHearAboutUs: ['', [Validators.required]],
      whoReferredYou: [''],
      describeHowYouHeardAboutUs: [''],
    });
    this.form2 = this.fb.group({
      businessIndustryId: [null, [Validators.required]],
      businessIndustryOthers: [''],
      customerTypes: [[], [Validators.required]],
      customerTypeOthers: [''],
      stateOfOrg: ['', [Validators.required]],
      address1: ['', [Validators.required]],
      address2: [''],
      city: ['', [Validators.required]],
      addressState: ['', [Validators.required]],
      zipCode: ['', [Validators.required]],
      website: [''],
      numberOfEmployeesId: [null, [Validators.required]],
      invoicingMethodId: [null, [Validators.required]],
      invoicingMethodOthers: [''],
      supportMethods: [[], [Validators.required]],
      // BELOW NOT IMPLEMENTED
      // yearFounded: [null, [Validators.required]],
      // dba: ['', [Validators.required]],
      // annualRevenue: [0, [Validators.required]],
    });

    const createAccount$ = this.store.select(selectAllCreateAccount);
    const businessProfile$ = this.store.select(selectAllBusinessProfile);
    let sub = createAccount$.subscribe((props) => {
      this.form1
        .get('registeredCompanyName')
        ?.setValue(props.registeredCompanyName);
      this.form1.get('howDidYouHearAboutUsId')?.setValue(props.heardAboutUsId);
      this.form1
        .get('howDidYouHearAboutUs')
        ?.setValue(props.howDidYouHearAboutUs);
      this.form1.get('whoReferredYou')?.setValue(props.heardAboutUsDetails);
      this.form1
        .get('describeHowYouHeardAboutUs')
        ?.setValue(props.heardAboutUsDetails);
    });
    sub.unsubscribe();
    sub = businessProfile$.subscribe((props) => {
      if (props.businessIndustry?.value) {
        this.businessIndustryChange({
          label: props.businessIndustry.value,
          value: props.businessIndustry.value,
          action: () => true,
        });
      }
      if (props.stateOfOrg?.value) {
        this.stateOfOrgChange({
          label: props.stateOfOrg.value,
          value: props.stateOfOrg.value,
          action: () => true,
        });
      }
      this.form2.get('address1')?.setValue(props.companyAddress.address1);
      this.form2.get('address2')?.setValue(props.companyAddress.address2);
      this.form2.get('city')?.setValue(props.companyAddress.city);
      if (props.companyAddress?.state) {
        this.addressStateChange({
          label: props.companyAddress.state,
          value: props.companyAddress.state,
          action: () => true,
        });
      }
      this.form2.get('zipCode')?.setValue(props.companyAddress.zipcode);
      this.numberOfEmployeesChange({
        label: props.employeeCount.value,
        value: props.employeeCount.value,
        action: () => true,
      });
      this.form2.get('website')?.setValue(props.companyWebsite);
    });
    sub.unsubscribe();

    this.isForm1 =
      this.resolveRegisteredCompanyNameText !== '' &&
      this.resolveRegisteredCompanyNameText !== null &&
      this.resolveRegisteredCompanyNameText !== undefined;
    this.isForm2 =
      this.resolveBusinessIndustryText !== '' &&
      this.resolveBusinessIndustryText !== null &&
      this.resolveBusinessIndustryText !== undefined;
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions,
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions,
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.backText =
      contentDict.get(ContentIDConstants.Back)?.displayText ?? 'Back';
    this.headerText =
      contentDict.get(ContentIDConstants.Header)?.displayText ??
      'Upgrade your existing profile';
    this.tooltip1Content =
      contentDict.get(ContentIDConstants.Tooltip1) ?? new PageContent();
    this.resolveRegisteredCompanyNameText =
      contentDict.get(ContentIDConstants.ResolveRegisteredCompanyName)
        ?.displayText ?? '';
    this.resolveHowDidYouHearAboutUsText =
      contentDict.get(ContentIDConstants.ResolveHowDidYouHearAboutUs)
        ?.displayText ?? '';
    this.resolveSetPassword1Text =
      contentDict.get(ContentIDConstants.ResolveSetPassword1)?.displayText ??
      '';
    this.resolveSetPassword2Text =
      contentDict.get(ContentIDConstants.ResolveSetPassword2)?.displayText ??
      '';
    this.resolveBusinessIndustryText =
      contentDict.get(ContentIDConstants.ResolveBusinessIndustry)
        ?.displayText ?? '';
    this.resolveCustomerTypeText =
      contentDict.get(ContentIDConstants.ResolveCustomerType)?.displayText ??
      '';
    this.resolveStateOfOrgText =
      contentDict.get(ContentIDConstants.ResolveStateOfOrg)?.displayText ?? '';
    this.resolveAddressText =
      contentDict.get(ContentIDConstants.ResolveAddress)?.displayText ?? '';
    this.resolveCompanyURLText =
      contentDict.get(ContentIDConstants.ResolveCompanyURL)?.displayText ?? '';
    this.resolveNumberOfEmployeesText =
      contentDict.get(ContentIDConstants.ResolveNumberOfEmployees)
        ?.displayText ?? '';
    this.resolveLendingMethodText =
      contentDict.get(ContentIDConstants.ResolveLendingMethod)?.displayText ??
      '';
    this.resolveInvoicingMethodText =
      contentDict.get(ContentIDConstants.ResolveInvoiceMethod)?.displayText ??
      '';
  }

  heardAboutUsChange = (option: IDropdownOption) => {
    if (option.label.toLowerCase().includes('other')) {
      this.heardAboutUsOtherBit = true;
    } else {
      this.heardAboutUsOtherBit = false;
    }
    if (option.label.toLowerCase().includes('refer')) {
      this.heardAboutUsReferralBit = true;
    } else {
      this.heardAboutUsReferralBit = false;
    }
    this.form1.get('howDidYouHearAboutUsId')?.setValue(option.value);
    this.form1.get('howDidYouHearAboutUs')?.setValue(option.label);
  };
  businessIndustryChange = (option: IDropdownOption) => {
    if (option.label.toLowerCase().includes('other')) {
      this.businessIndustryOthersBit = true;
    } else {
      this.businessIndustryOthersBit = false;
    }
    this.form2.get('businessIndustryId')?.setValue(option.value);
  };
  stateOfOrgChange = (option: IDropdownOption) => {
    this.defaultStateOfOrg = option;
    this.form2.get('stateOfOrg')?.setValue(option);
  };
  addressStateChange = (option: IDropdownOption) => {
    this.defaultAddressState = option;
    this.form2.get('addressState')?.setValue(option);
  };
  numberOfEmployeesChange = (option: IDropdownOption) => {
    this.form2.get('numberOfEmployeesId')?.setValue(option.value);
  };
  invoicingMethodChange = (option: IDropdownOption) => {
    if (option.label.toLowerCase().includes('other')) {
      this.invoicingMethodOthersBit = true;
    } else {
      this.invoicingMethodOthersBit = false;
    }
    this.form2.get('invoicingMethodId')?.setValue(option.value);
  };

  mapOptionsToDropdownOptions = (options: IOptions[]): IDropdownOption[] =>
    options.map(
      (option: IOptions) =>
        ({
          label: option.value,
          value: option.id,
          action: () => {},
        }) as IDropdownOption,
    );

  mapStatesToDropdownOptions = (states: IStateProvince[]): IDropdownOption[] =>
    states.map(
      (state: IStateProvince) =>
        ({
          label: state.name,
          value: state.stateProvinceId,
          action: () => {},
        }) as IDropdownOption,
    );

  handleCustomerTypesOutput(option: IMultiOptions) {
    let tempArr: IMultiOptions[] = this.form2.get('customerTypes')?.value;
    let hadId = false;
    let containsOther = false;
    tempArr = tempArr.filter((tempOption: IMultiOptions) => {
      if (tempOption.id === option.id) {
        if (option.name.toLowerCase().includes('other')) {
          containsOther = false;
        }
        hadId = true;
        return false;
      }
      if (option.name.toLowerCase().includes('other')) {
        containsOther = true;
      }
      return true;
    });
    this.customerTypeOthersBit = containsOther;
    if (!hadId) tempArr.push({ ...option, selected: true });
    this.form2.get('customerTypes')?.setValue(tempArr);
  }

  handleLendingMethodsOutput(option: IMultiOptions) {
    let tempArr: IMultiOptions[] = this.form2.get('supportMethods')?.value;
    let hadId = false;
    tempArr = tempArr.filter((tempOption: IMultiOptions) => {
      if (tempOption.id === option.id) {
        hadId = true;
        return false;
      }
      return true;
    });
    if (!hadId) tempArr.push({ ...option, selected: true });
    this.form2.get('supportMethods')?.setValue(tempArr);
  }

  async submit() {
    // this.dispatchForm(this.form);
    // TODO move activation to API-side LU Navigator
    if (this.form1.valid && this.isForm1) {
      const payload = this.buildPayload();
      if (payload.howDidYouHearAboutUsId) {
        this.ees.updateDashboardStateId(this.nextState.id);
      }
      const result = await this.lus.saveLegacyUpgrade(payload);
      await this.hs.HydrateOnboardingStore(
        HydrationRoles.onboardingProspectOrOnboardingClient,
        HydrationSources.legacyUpgrade,
      );
      return result.isSuccessful;
    }

    if (this.form2.valid && this.isForm2) {
      const payload = this.buildPayload();
      if (payload.industryBusinessId) {
        this.ees.updateDashboardStateId(this.nextState.id);
      }
      const result = await this.lus.saveLegacyUpgrade(payload);
      await this.hs.HydrateOnboardingStore(
        HydrationRoles.onboardingProspectOrOnboardingClient,
        HydrationSources.legacyUpgrade,
      );
      return result.isSuccessful;
    }
    return false;
  }

  private buildPayload(): ILegacyUpgrade {
    return {
      registerCompanyName: this.form1.get('registeredCompanyName')?.value ?? '',
      howDidYouHearAboutUsId:
        this.form1.get('howDidYouHearAboutUsId')?.value ?? null,
      howDidYouHearAboutUs: this.form1.get('howDidYouHearAboutUs')?.value ?? '',
      whoReferredYou: this.form1.get('whoReferredYou')?.value ?? '',
      describeHowYouHeardAboutUs:
        this.form1.get('describeHowYouHeardAboutUs')?.value ?? '',
      password: this.form1.get('password')?.value ?? '',
      industryBusinessId: this.form2.get('businessIndustryId')?.value ?? null,
      industryBusinessOthers:
        this.form2.get('businessIndustryOthers')?.value ?? '',
      customerTypes: this.form2.get('customerTypes')?.value ?? [],
      stateOfOrganization: this.form2.get('stateOfOrg')?.value.value ?? '',
      address1: this.form2.get('address1')?.value ?? '',
      address2: this.form2.get('address2')?.value ?? '',
      city: this.form2.get('city')?.value ?? '',
      state: this.form2.get('addressState')?.value?.value ?? '',
      zipCode: this.form2.get('zipCode')?.value ?? '',
      website: this.form2.get('website')?.value ?? '',
      numberOfEmployeesId: this.form2.get('numberOfEmployeesId')?.value ?? null,
      invoicingMethodId: this.form2.get('invoicingMethodId')?.value ?? null,
      invoicingMethodOthers:
        this.form2.get('invoicingMethodOthers')?.value ?? '',
      supportMethods: this.form2.get('supportMethods')?.value ?? [],
    } as ILegacyUpgrade;
  }

  private dispatchForm(form: FormGroup): void {
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(
      CreateAccountActions.LegacyUpgradeAction({
        registeredCompanyName: form.get('registeredCompanyName')?.value ?? '',
        heardAboutUsId: form.get('howDidYouHearAboutUs')?.value?.value ?? '',
        howDidYouHearAboutUs:
          form.get('howDidYouHearAboutUs')?.value.label ?? '',
        heardAboutUsDetails:
          form.get('whoReferredYou')?.value ??
          form.get('describeHowYouHeardAboutUs')?.value ??
          '',
      }),
    );

    const addressObj = new Address();
    addressObj.buildAddress({
      address1: form.get('address1')?.value ?? '',
      address2: form.get('address2')?.value ?? '',
      city: form.get('city')?.value ?? '',
      state: form.get('addressState')?.value ?? '',
      zipcode: form.get('zipCode')?.value ?? '',
    });
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(
      BusinessProfileActions.LegacyUpgradeAction({
        businessIndustry: {
          id: (form.get('businessIndustryId')?.value as string | number) ?? 0,
          value: '',
        } as IIdValuePair,
        customerTypes:
          (form.get('customerTypes')?.value as IMultiOptions[]) ?? [],
        stateOfOrg: {
          id: (form.get('stateOfOrg')?.value as string | number) ?? '',
          value: '',
        } as IIdValuePair,
        companyAddress: addressObj,
        companyWebsite: (form.get('website')?.value as string) ?? '',
        employeeCount: {
          id: (form.get('numberOfEmployeesId')?.value as string | number) ?? 0,
          value: '',
        } as IIdValuePair,
        invoicingMethod: {
          id: (form.get('invoicingMethodId')?.value as string | number) ?? 0,
          value: '',
        } as IIdValuePair,
        supportMethods:
          (form.get('supportMethods')?.value as IMultiOptions[]) ?? [],
        dba: (form.get('dba')?.value as string) ?? '',
        yearFounded: (form.get('yearFounded')?.value as number) ?? 0,
        annualRevenue: {
          id: (form.get('annualRevenue')?.value as string | number) ?? 0,
          value: '',
        } as IIdValuePair,
      }),
    );
  }
}
